
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Target,
  Flame,
  Calendar,
  Scale,
  Timer,
  UtensilsCrossed,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Plus,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserDietOverview } from '@/components/diet-plans-v2/user-diet-overview';
import {
  useUserDietProfile,
  useWeightHistory,
  useAddWeightLog,
  useUserFastingLogs,
  useFastingStats,
  useActiveFasting,
  useStartFasting,
  useEndFasting,
  useUserDailyIntakes,
} from '@/api/user-diet-profiles';

interface UserHealthDashboardProps {
  userId: string;
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function StatItem({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon?: React.ElementType;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </span>
      <span className="text-xl font-bold leading-none">{value}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

// ─── Weight Tab ───────────────────────────────────────────────────────────────

function WeightTab({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  const [notesInput, setNotesInput] = useState('');

  const { data: profile } = useUserDietProfile(userId);
  const { data: weightLogs = [], isLoading } = useWeightHistory(userId);
  const addWeight = useAddWeightLog();

  const sorted = [...weightLogs].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const chartData = sorted.map((log) => ({
    date: format(new Date(log.date), 'MMM d'),
    weight: log.weight,
  }));

  const latest = sorted[sorted.length - 1];
  const previous = sorted[sorted.length - 2];
  const delta = latest && previous ? latest.weight - previous.weight : null;

  const handleLogWeight = () => {
    const w = parseFloat(weightInput);
    if (!w || isNaN(w)) return;
    addWeight.mutate(
      { userId, weight: w, notes: notesInput || undefined },
      {
        onSuccess: () => {
          setShowLogDialog(false);
          setWeightInput('');
          setNotesInput('');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  const currentWeight = latest?.weight ?? profile?.currentWeight;
  const targetWeight = profile?.targetWeight;

  return (
    <div className="space-y-4">
      {/* Chart + stats in one card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>{t('health.weightProgress', 'Weight Progress')}</CardTitle>
              {profile?.goalType && (
                <p className="text-sm text-muted-foreground mt-0.5 capitalize">
                  {profile.goalType.replace('-', ' ')}
                </p>
              )}
            </div>
            <Button size="sm" onClick={() => setShowLogDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              {t('health.logWeight', 'Log Weight')}
            </Button>
          </div>

          {/* Inline stats strip */}
          <div className="flex flex-wrap gap-6 pt-2">
            <StatItem
              label={t('health.currentWeight', 'Current')}
              value={currentWeight ? `${currentWeight} kg` : '—'}
              sub={profile?.bmi ? `BMI ${profile.bmi}` : undefined}
              icon={Scale}
            />
            <Separator orientation="vertical" className="h-10 self-center" />
            <StatItem
              label={t('health.targetWeight', 'Target')}
              value={targetWeight ? `${targetWeight} kg` : '—'}
              sub={
                currentWeight && targetWeight
                  ? `${Math.abs(currentWeight - targetWeight).toFixed(1)} kg ${currentWeight > targetWeight ? t('health.toLose', 'to lose') : t('health.toGain', 'to gain')}`
                  : undefined
              }
              icon={Target}
            />
            {delta !== null && (
              <>
                <Separator orientation="vertical" className="h-10 self-center" />
                <StatItem
                  label={t('health.lastChange', 'Last Change')}
                  value={`${delta > 0 ? '+' : ''}${delta.toFixed(1)} kg`}
                  sub={t('health.sinceLastLog', 'since last log')}
                  icon={delta > 0 ? TrendingUp : TrendingDown}
                />
              </>
            )}
            {weightLogs.length > 0 && (
              <>
                <Separator orientation="vertical" className="h-10 self-center" />
                <StatItem
                  label={t('health.totalLogs', 'Logs')}
                  value={weightLogs.length}
                  sub={t('health.entries', 'entries')}
                />
              </>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {chartData.length < 2 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Scale className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">
                {t('health.noWeightData', 'Log at least two entries to see the progress chart.')}
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} unit=" kg" />
                <Tooltip
                  formatter={(v: number | string | undefined) => [`${v ?? ''} kg`, t('health.weight', 'Weight')]}
                />
                {targetWeight && (
                  <ReferenceLine
                    y={targetWeight}
                    stroke="hsl(var(--primary))"
                    strokeDasharray="4 4"
                    label={{ value: 'Goal', position: 'right', fontSize: 11 }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* History table */}
      {weightLogs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {t('health.weightHistory', 'Weight History')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.date', 'Date')}</TableHead>
                  <TableHead>{t('health.weight', 'Weight')}</TableHead>
                  <TableHead className="hidden sm:table-cell">
                    {t('common.notes', 'Notes')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...weightLogs]
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((log) => (
                    <TableRow key={log._id}>
                      <TableCell className="text-sm">
                        {format(new Date(log.date), 'PP')}
                      </TableCell>
                      <TableCell className="font-medium">{log.weight} kg</TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                        {log.notes || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Log Weight Dialog */}
      <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('health.logWeight', 'Log Weight')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>
                {t('health.weight', 'Weight')} (kg)
              </Label>
              <Input
                type="number"
                step="0.1"
                min="1"
                placeholder="e.g. 75.5"
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t('common.notes', 'Notes')}{' '}
                <span className="text-muted-foreground font-normal">
                  ({t('common.optional', 'optional')})
                </span>
              </Label>
              <Textarea
                placeholder={t('health.weightNotesPlaceholder', 'Morning weight, after workout...')}
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleLogWeight} disabled={!weightInput || addWeight.isPending}>
              {addWeight.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Fasting Tab ──────────────────────────────────────────────────────────────

const FASTING_TYPES = [
  { value: '16-8', label: '16:8 (16h fast)' },
  { value: '18-6', label: '18:6 (18h fast)' },
  { value: '20-4', label: '20:4 (20h fast)' },
  { value: '24', label: '24h fast' },
  { value: 'custom', label: 'Custom' },
];

const FASTING_DURATIONS: Record<string, number> = {
  '16-8': 960,
  '18-6': 1080,
  '20-4': 1200,
  '24': 1440,
  custom: 720,
};

function FastingTab({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [fastingType, setFastingType] = useState('16-8');
  const [customDuration, setCustomDuration] = useState('');
  const [endNotes, setEndNotes] = useState('');

  const { data: stats, isLoading: statsLoading } = useFastingStats(userId);
  const { data: logs = [], isLoading: logsLoading } = useUserFastingLogs(userId, undefined, 20);
  const { data: activeFast } = useActiveFasting(userId);
  const startFasting = useStartFasting();
  const endFasting = useEndFasting();

  const handleStartFast = () => {
    const duration =
      fastingType === 'custom'
        ? parseInt(customDuration) * 60
        : FASTING_DURATIONS[fastingType];
    startFasting.mutate(
      { userId, fastingType, targetDuration: duration },
      { onSuccess: () => setShowStartDialog(false) }
    );
  };

  const handleEndFast = () => {
    if (!activeFast) return;
    endFasting.mutate(
      { fastingId: activeFast._id, userId, notes: endNotes || undefined },
      {
        onSuccess: () => {
          setShowEndDialog(false);
          setEndNotes('');
        },
      }
    );
  };

  const formatDuration = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (statsLoading || logsLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Overview card: stats + active status + action */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('health.fasting', 'Fasting')}</CardTitle>
            {!activeFast ? (
              <Button size="sm" onClick={() => setShowStartDialog(true)}>
                <Timer className="h-4 w-4 mr-1" />
                {t('health.startFast', 'Start Fast')}
              </Button>
            ) : (
              <Button size="sm" variant="destructive" onClick={() => setShowEndDialog(true)}>
                {t('health.endFast', 'End Fast')}
              </Button>
            )}
          </div>

          {/* Inline stats */}
          <div className="flex flex-wrap gap-6 pt-2">
            <StatItem
              label={t('health.currentStreak', 'Day Streak')}
              value={stats?.currentStreak ?? 0}
              icon={Timer}
            />
            <Separator orientation="vertical" className="h-10 self-center" />
            <StatItem
              label={t('health.longestFast', 'Longest Fast')}
              value={stats?.longestFast ? formatDuration(stats.longestFast) : '—'}
            />
            <Separator orientation="vertical" className="h-10 self-center" />
            <StatItem
              label={t('health.totalFasts', 'Total Fasts')}
              value={stats?.totalFasts ?? 0}
            />
            {stats?.completedFasts !== undefined && (
              <>
                <Separator orientation="vertical" className="h-10 self-center" />
                <StatItem
                  label={t('health.completed', 'Completed')}
                  value={stats.completedFasts}
                />
              </>
            )}
          </div>
        </CardHeader>

        {/* Active fast info */}
        {activeFast && (
          <CardContent className="pt-0">
            <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5">
              <Timer className="h-5 w-5 text-primary shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm">
                  {activeFast.fastingType.toUpperCase()}{' '}
                  {t('health.fastInProgress', 'fast in progress')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('health.started', 'Started')}:{' '}
                  {format(new Date(activeFast.startTime), 'PPp')} &nbsp;·&nbsp;{' '}
                  {t('health.target', 'Target')}: {formatDuration(activeFast.targetDuration)}
                </p>
              </div>
            </div>
          </CardContent>
        )}

        {!activeFast && logs.length === 0 && (
          <CardContent className="pt-0">
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Timer className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">
                {t('health.noFastingData', 'No fasting records yet. Start your first fast!')}
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* History table */}
      {logs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {t('health.fastingHistory', 'Fasting History')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.date', 'Date')}</TableHead>
                  <TableHead>{t('health.type', 'Type')}</TableHead>
                  <TableHead>{t('health.duration', 'Duration')}</TableHead>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log._id}>
                    <TableCell className="text-sm">
                      {format(new Date(log.startTime), 'PP')}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{log.fastingType.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.duration ? formatDuration(log.duration) : '—'}
                    </TableCell>
                    <TableCell>
                      {log.completed ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {t('health.completed', 'Completed')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <XCircle className="h-3.5 w-3.5" />
                          {t('health.incomplete', 'Incomplete')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Start Fast Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('health.startFast', 'Start Fast')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('health.fastingType', 'Fasting Type')}</Label>
              <Select value={fastingType} onValueChange={setFastingType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FASTING_TYPES.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>
                      {ft.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {fastingType === 'custom' && (
              <div className="space-y-2">
                <Label>{t('health.durationHours', 'Duration (hours)')}</Label>
                <Input
                  type="number"
                  min="1"
                  max="72"
                  placeholder="e.g. 20"
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleStartFast}
              disabled={startFasting.isPending || (fastingType === 'custom' && !customDuration)}
            >
              {startFasting.isPending ? t('common.saving') : t('health.startFast', 'Start Fast')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Fast Dialog */}
      <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('health.endFast', 'End Fast')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>
                {t('common.notes', 'Notes')}{' '}
                <span className="text-muted-foreground font-normal">
                  ({t('common.optional', 'optional')})
                </span>
              </Label>
              <Textarea
                placeholder={t('health.fastNotesPlaceholder', 'How did it go?')}
                value={endNotes}
                onChange={(e) => setEndNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleEndFast}
              disabled={endFasting.isPending}
            >
              {endFasting.isPending ? t('common.saving') : t('health.endFast', 'End Fast')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Food Intake Tab ──────────────────────────────────────────────────────────

function MacroChip({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {label}: {Math.round(value)}g
    </span>
  );
}

interface DailyIntakeRecord {
  _id: string;
  date: string;
  totalCalories: number;
  totalMacros: { protein: number; carbs: number; fat: number; fiber?: number };
  meals: Array<{
    type: string;
    time: string;
    foods: Array<{ name: string; quantity: number; unit: string; calories: number }>;
  }>;
  assignedPlanId?: string;
}

function IntakeRow({ intake }: { intake: DailyIntakeRecord }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { protein = 0, carbs = 0, fat = 0 } = intake.totalMacros ?? {};

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-medium text-sm truncate">
            {format(new Date(intake.date), 'EEE, MMM d, yyyy')}
          </span>
          {intake.assignedPlanId && (
            <Badge variant="outline" className="text-xs shrink-0">
              {t('health.followingPlan', 'Following Plan')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className="font-semibold text-sm">
            {Math.round(intake.totalCalories)} kcal
          </span>
          <div className="hidden sm:flex gap-1.5">
            <MacroChip label="P" value={protein} color="#3b82f6" />
            <MacroChip label="C" value={carbs} color="#f59e0b" />
            <MacroChip label="F" value={fat} color="#ef4444" />
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-muted/20">
          {/* Macros on mobile */}
          <div className="flex flex-wrap gap-2 sm:hidden">
            <MacroChip label="Protein" value={protein} color="#3b82f6" />
            <MacroChip label="Carbs" value={carbs} color="#f59e0b" />
            <MacroChip label="Fat" value={fat} color="#ef4444" />
          </div>

          {intake.meals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No meals logged.</p>
          ) : (
            intake.meals.map((meal, i) => (
              <div key={i} className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {meal.type}
                  {meal.time ? ` · ${meal.time}` : ''}
                </p>
                <ul className="space-y-0.5">
                  {meal.foods.map((food, fi) => (
                    <li key={fi} className="flex justify-between text-sm">
                      <span>{food.name}</span>
                      <span className="text-muted-foreground ml-4 shrink-0">
                        {food.quantity} · {Math.round(food.calories)} kcal
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FoodIntakeTab({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const { data: profile } = useUserDietProfile(userId);
  const { data: intakes = [], isLoading } = useUserDailyIntakes(userId);

  const targetCalories = profile?.tdee ?? 0;

  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  const sorted = [...intakes].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <UtensilsCrossed className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm">
          {t('health.noIntakeData', 'No food intake records found.')}
        </p>
      </div>
    );
  }

  const last7 = sorted.slice(0, 7);
  const avgCalories = Math.round(last7.reduce((s, d) => s + d.totalCalories, 0) / last7.length);
  const avgProtein = Math.round(last7.reduce((s, d) => s + (d.totalMacros?.protein ?? 0), 0) / last7.length);
  const avgCarbs = Math.round(last7.reduce((s, d) => s + (d.totalMacros?.carbs ?? 0), 0) / last7.length);
  const avgFat = Math.round(last7.reduce((s, d) => s + (d.totalMacros?.fat ?? 0), 0) / last7.length);

  return (
    <div className="space-y-4">
      {/* 7-day summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t('health.recentAverage', '7-Day Average')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Flame className="h-3 w-3" />
                {t('health.calories', 'Calories')}
              </p>
              <p className="text-2xl font-bold">{avgCalories}</p>
              {targetCalories > 0 && (
                <div className="space-y-0.5">
                  <Progress
                    value={Math.min((avgCalories / targetCalories) * 100, 100)}
                    className="h-1.5"
                  />
                  <p className="text-xs text-muted-foreground">
                    of {targetCalories} kcal target
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {t('health.protein', 'Protein')}
              </p>
              <p className="text-2xl font-bold">{avgProtein}g</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {t('health.carbs', 'Carbs')}
              </p>
              <p className="text-2xl font-bold">{avgCarbs}g</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {t('health.fat', 'Fat')}
              </p>
              <p className="text-2xl font-bold">{avgFat}g</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily log list */}
      <div className="space-y-2">
        {sorted.map((intake) => (
          <IntakeRow key={intake._id} intake={intake as DailyIntakeRecord} />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function UserHealthDashboard({ userId }: UserHealthDashboardProps) {
  const { t } = useTranslation();

  return (
    <Tabs defaultValue="dietPlans">
      <TabsList className="mb-4">
        <TabsTrigger value="dietPlans">
          <Calendar className="h-4 w-4 mr-1.5" />
          {t('health.dietPlans', 'Diet Plans')}
        </TabsTrigger>
        <TabsTrigger value="weight">
          <Scale className="h-4 w-4 mr-1.5" />
          {t('health.weight', 'Weight')}
        </TabsTrigger>
        <TabsTrigger value="fasting">
          <Timer className="h-4 w-4 mr-1.5" />
          {t('health.fasting', 'Fasting')}
        </TabsTrigger>
        <TabsTrigger value="foodIntake">
          <UtensilsCrossed className="h-4 w-4 mr-1.5" />
          {t('health.foodIntake', 'Food Intake')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="dietPlans">
        <UserDietOverview userId={userId} />
      </TabsContent>

      <TabsContent value="weight">
        <WeightTab userId={userId} />
      </TabsContent>

      <TabsContent value="fasting">
        <FastingTab userId={userId} />
      </TabsContent>

      <TabsContent value="foodIntake">
        <FoodIntakeTab userId={userId} />
      </TabsContent>
    </Tabs>
  );
}
